/**
 * Cutting a building into sheets. A cut is an axis-aligned line; circulation cells go to the
 * side they lie on and each room goes whole to the side its centroid lies on, so the boundary
 * jogs around any room the line crosses. Cuts are chosen to need the fewest sheets, then to
 * cross the least circulation and the fewest rooms - which puts them across corridors and the
 * narrow links between wings, where a drafter puts a matchline.
 */

import type { Raster } from "./footprint.ts";
import type { Room } from "./rooms.ts";

export interface Group {
  readonly building: number;
  readonly rooms: readonly Room[];
  readonly circulation: Int32Array;
}

export interface Box {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export interface Window {
  /** Model inches the viewport shows across and up. */
  readonly width: number;
  readonly height: number;
  /** Model inches kept clear on every side of a sheet's content. */
  readonly margin: number;
}

/** A room crossed by the cut line costs as much as this many cells of corridor. */
const STRADDLE_COST_INCHES = 240;
/**
 * Neither side of a cut may be thinner than this share of the viewport. Without it the
 * cheapest cut is often a few feet in from an outside wall, where there is almost no
 * corridor to cross, and the result is a sheet showing a sliver.
 */
const MIN_SIDE_FRACTION = 0.25;

export function groupBox(group: Group, width: number): Box | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const room of group.rooms) {
    minX = Math.min(minX, room.minX);
    minY = Math.min(minY, room.minY);
    maxX = Math.max(maxX, room.maxX);
    maxY = Math.max(maxY, room.maxY);
  }
  for (const i of group.circulation) {
    const x = i % width;
    const y = (i - x) / width;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
}

function sheetsFor(box: Box, raster: Raster, window: Window): number {
  const w = (box.maxX - box.minX + 1) * raster.cell + 2 * window.margin;
  const h = (box.maxY - box.minY + 1) * raster.cell + 2 * window.margin;
  return Math.ceil(w / window.width) * Math.ceil(h / window.height);
}

export function fits(box: Box, raster: Raster, window: Window): boolean {
  return sheetsFor(box, raster, window) === 1;
}

interface Cut {
  readonly axis: "x" | "y";
  /** Items whose coordinate is below this go to the low side. */
  readonly at: number;
  readonly sheets: number;
  readonly cost: number;
  readonly centreDistance: number;
}

function bestCut(group: Group, box: Box, raster: Raster, window: Window, minSideFraction: number): Cut | null {
  const width = raster.shape.width;
  let best: Cut | null = null;
  for (const axis of ["x", "y"] as const) {
    const lo = axis === "x" ? box.minX : box.minY;
    const hi = axis === "x" ? box.maxX : box.maxY;
    const n = hi - lo + 1;
    if (n < 2) continue;
    const minSpan = minSideFraction * (axis === "x" ? window.width : window.height);
    // Per coordinate along the axis: the extent across it and how many items sit there.
    const minB = new Float64Array(n).fill(Infinity);
    const maxB = new Float64Array(n).fill(-Infinity);
    const minA = new Float64Array(n).fill(Infinity);
    const maxA = new Float64Array(n).fill(-Infinity);
    const count = new Int32Array(n);
    const corridor = new Int32Array(n);
    const straddle = new Int32Array(n + 1);
    const mark = (k: number, a0: number, a1: number, b0: number, b1: number) => {
      if (a0 < minA[k]!) minA[k] = a0;
      if (a1 > maxA[k]!) maxA[k] = a1;
      if (b0 < minB[k]!) minB[k] = b0;
      if (b1 > maxB[k]!) maxB[k] = b1;
      count[k]! += 1;
    };
    for (const i of group.circulation) {
      const x = i % width;
      const y = (i - x) / width;
      const a = axis === "x" ? x : y;
      const b = axis === "x" ? y : x;
      mark(a - lo, a, a, b, b);
      corridor[a - lo]! += 1;
    }
    for (const room of group.rooms) {
      const a0 = axis === "x" ? room.minX : room.minY;
      const a1 = axis === "x" ? room.maxX : room.maxY;
      const b0 = axis === "x" ? room.minY : room.minX;
      const b1 = axis === "x" ? room.maxY : room.maxX;
      const centre = axis === "x" ? room.centroidX : room.centroidY;
      mark(Math.min(n - 1, Math.max(0, Math.floor(centre) - lo)), a0, a1, b0, b1);
      // Crossed by any cut at a coordinate in (a0, a1].
      straddle[a0 - lo + 1]! += 1;
      straddle[a1 - lo + 1]! -= 1;
    }
    for (let k = 1; k <= n; k++) straddle[k]! += straddle[k - 1]!;

    const extend = (
      b: { minX: number; minY: number; maxX: number; maxY: number } | null,
      k: number,
    ) => {
      if (count[k] === 0) return b;
      const next = b ?? { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
      if (axis === "x") {
        next.minX = Math.min(next.minX, minA[k]!);
        next.maxX = Math.max(next.maxX, maxA[k]!);
        next.minY = Math.min(next.minY, minB[k]!);
        next.maxY = Math.max(next.maxY, maxB[k]!);
      } else {
        next.minY = Math.min(next.minY, minA[k]!);
        next.maxY = Math.max(next.maxY, maxA[k]!);
        next.minX = Math.min(next.minX, minB[k]!);
        next.maxX = Math.max(next.maxX, maxB[k]!);
      }
      return next;
    };
    // Suffix boxes, computed back to front once.
    const high: (Box | null)[] = new Array(n + 1).fill(null);
    let running: { minX: number; minY: number; maxX: number; maxY: number } | null = null;
    for (let k = n - 1; k >= 0; k--) {
      running = extend(running === null ? null : { ...running }, k);
      high[k] = running === null ? null : { ...running };
    }

    const straddleCells = STRADDLE_COST_INCHES / raster.cell;
    let low: { minX: number; minY: number; maxX: number; maxY: number } | null = null;
    for (let k = 1; k < n; k++) {
      low = extend(low === null ? null : { ...low }, k - 1);
      const up = high[k];
      if (low === null || up === null || up === undefined) continue;
      const span = (b: Box) => ((axis === "x" ? b.maxX - b.minX : b.maxY - b.minY) + 1) * raster.cell;
      if (Math.min(span(low), span(up)) < minSpan) continue;
      // A cut whose larger side is still about as long as the whole range buys nothing. That is
      // a room wider than a sheet, and cutting beside it only peels walls off into slivers.
      if (Math.max(span(low), span(up)) > n * raster.cell - 2 * window.margin) continue;
      const sheets = sheetsFor(low, raster, window) + sheetsFor(up, raster, window);
      const cost = corridor[k]! + straddle[k]! * straddleCells;
      const centreDistance = Math.abs(k - n / 2) / n;
      const candidate: Cut = { axis, at: lo + k, sheets, cost, centreDistance };
      if (
        best === null ||
        sheets < best.sheets ||
        (sheets === best.sheets && cost < best.cost) ||
        (sheets === best.sheets && cost === best.cost && centreDistance < best.centreDistance)
      ) {
        best = candidate;
      }
    }
  }
  return best;
}

function apply(group: Group, cut: Cut, width: number): [Group, Group] {
  const lowRooms: Room[] = [];
  const highRooms: Room[] = [];
  for (const room of group.rooms) {
    const centre = cut.axis === "x" ? room.centroidX : room.centroidY;
    (Math.floor(centre) < cut.at ? lowRooms : highRooms).push(room);
  }
  const lowCells: number[] = [];
  const highCells: number[] = [];
  for (const i of group.circulation) {
    const x = i % width;
    const a = cut.axis === "x" ? x : (i - x) / width;
    (a < cut.at ? lowCells : highCells).push(i);
  }
  return [
    { building: group.building, rooms: lowRooms, circulation: Int32Array.from(lowCells) },
    { building: group.building, rooms: highRooms, circulation: Int32Array.from(highCells) },
  ];
}

export interface Partition {
  /** In sheet order: left before right, top before bottom. */
  readonly groups: readonly Group[];
  readonly oversized: number;
}

export function partition(building: Group, raster: Raster, window: Window): Partition {
  const width = raster.shape.width;
  const groups: Group[] = [];
  let oversized = 0;
  const visit = (group: Group, depth: number) => {
    const box = groupBox(group, width);
    if (box === null) return;
    if (fits(box, raster, window)) {
      groups.push(group);
      return;
    }
    const cut =
      depth < 16
        ? bestCut(group, box, raster, window, MIN_SIDE_FRACTION) ?? bestCut(group, box, raster, window, 0)
        : null;
    if (cut === null) {
      oversized++;
      groups.push(group);
      return;
    }
    const [low, high] = apply(group, cut, width);
    // Reading order: a vertical cut gives left then right, a horizontal one top then bottom.
    const [first, second] = cut.axis === "x" ? [low, high] : [high, low];
    visit(first, depth + 1);
    visit(second, depth + 1);
  };
  visit(building, 0);
  return { groups, oversized };
}
